<template>
  <v-layout column class="mx-4" id="careplan">
    <mrx-triage></mrx-triage>
    <template
      v-for="category in categories"
    >
      <post-assessment-card
        v-if="getContentList(category).length > 0"
        :key="category"
        :headerToken="getHeaderToken(category)"
        :contentList="getContentList(category)"
      ></post-assessment-card>
    </template>
    <!-- Copy all button -->
    <div
      class="d-flex flex-column align-center pt-2 pb-2"
      style="margin: 0 auto; width: 100%;"
    >
      <CopyClipboardButton
        token="app.careplan.button.copy.title"
        name="careplan"
        cssClass="copy-all-btn"
        :payload="buildCarePlanCopyPayload()"
        mode="button"
        style="width: 100%;"
      />
    </div>
  </v-layout>
</template>

<script>
import languageLogic from "@/mixins/languageLogic"
import mrx from "@/mixins/mrx"
import PostAssessmentCard from "../layouts/PostAssessmentCard.vue"
import { mapGetters } from 'vuex'
import CopyClipboardButton from '@/components/widgets/CopyClipboardButton'
import { buildCarePlanCopyPayload } from '../../utils/carePlanUtils.js'

function getHighestPriority(list) {
  return list.sort((a,b) => b.priority - a.priority)[0]
}

function filterByGroupPriority(list) {
  const finalList = []
  const groups = {}
  list.forEach(item => {
    if (!item.group) finalList.push(item)
    else {
      groups[item.group] ||= []
      groups[item.group].push(item)
    }
  })
  const highestFromGroups = Object.values(groups).map(getHighestPriority)
  return finalList.concat(highestFromGroups)
}

export { filterByGroupPriority }

export default {
  name: "CarePlan",
  components: { PostAssessmentCard, CopyClipboardButton },
  mixins: [languageLogic, mrx],
  data() {
    return {
      categories: [
        "perform",
        "medication",
        "treatment_instructions",
        "referral",
        "return_reevaluation",
        "follow_up_visit",
      ],
    }
  },
  computed: {
    ...mapGetters({
      selectedTreatments: 'v4/mrx/getSelectedTreatments',
      plansByCategory: 'v4/mrx/getUniquePlansByCategory',
      hasTreatments: 'v4/mrx/hasTreatmentsToShow'
    }),
    hasSelectedTreatments() {
      return this.selectedTreatments().length > 0
    },
  },
  mounted() {
    window.scroll(0, 0)
    if (this.hasTreatments && !this.hasSelectedTreatments) {
      this.$track(this.$analyticsEvents.MEDICATION_NONE_SELECTED, {
        component: 'CarePlan',
        has_available_treatments: this.hasTreatments
      })
    }
  },
  methods: {
    getHeaderToken(category) {
      if (category === 'medication') {
        return `care.category.treatment`
      }
      else {
        return `care.category.${category}`
      }
    },
    getContentList(category) {
      if (category === 'medication') {
        return this.getMedicationContent()
      }
      else {
        return this.getNonMedicationContent(category)
      }
    },
    getMedicationContent() {
      if (!this.hasTreatments) {
        return [{
          headerToken: 'app.careplan.message.no_treatments'
        }]
      }
      else if (!this.hasSelectedTreatments) {
        return [{
          headerToken: 'app.careplan.message.no_treatments_selected'
        }]
      }
      else {
        const selectedTreatments = this.selectedTreatments()
        const filteredTreatments = filterByGroupPriority(selectedTreatments)
        return this.formatTreatments(filteredTreatments)
      }
    },
    getNonMedicationContent(category) {
      const uniquePlans = this.plansByCategory(category)
      const filteredPlans = filterByGroupPriority(uniquePlans)
      return filteredPlans.map(plan => ({
        name: plan.name,
        detailContent: [`care.actions.${plan.name}`],
        useHTMLFormat: false
      }))
    },
    formatTreatments(treatments) {
      return treatments.map(treatment => {
        const content = this.convertTreatmentToString(treatment)

        return {
          detailContent: [content],
          headerToken: `treatment.${treatment.name}`,
          name: treatment.name,
          useHTMLFormat: true,
        }
      })
    },

    buildCarePlanCopyPayload() {
      return buildCarePlanCopyPayload(this);
    }
  },
}
</script>